import React, {useEffect} from "react";
import {BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {connect} from "react-redux";
import OneSignal from "react-onesignal";
import Echo from "laravel-echo";
import Socketio from "socket.io-client";
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import SigninScreen from "../screens/signup/SigninScreen";
import ForgotPasswordScreen from "../screens/signup/ForgotPasswordScreen";
import ResetPasswordScreen from "../screens/signup/ResetPasswordScreen";
import SetNewPasswordScreen from "../screens/signup/SetNewPasswordScreen";

import DashboardScreen from "../screens/dashboard/DashboardScreen";
import BookingsScreen from "../screens/dashboard/BookingsScreen";
import EarningsScreen from "../screens/dashboard/EarningsScreen";
import MessagesScreen from "../screens/dashboard/MessagesScreen";
import RoomsScreen from "../screens/dashboard/RoomsScreen";
import SettingsScreen from "../screens/dashboard/SettingsScreen";
import HelpScreen from "../screens/dashboard/HelpScreen";
import NewStudioScreen from "../screens/dashboard/NewStudioScreen";
import NewRoomScreen from "../screens/dashboard/NewRoomScreen";
import StudiosScreen from "../screens/dashboard/StudiosScreen";
import NotificationsScreen from "../screens/dashboard/NotificationsScreen";
import ClientsScreen from "../screens/dashboard/ClientsScreen";
import DataScreen from "../screens/dashboard/DataScreen";
import RevenueScreen from "../screens/dashboard/RevenueScreen";
import ReportsScreen from "../screens/dashboard/ReportsScreen";
import PayoutsScreen from "../screens/dashboard/PayoutsScreen";
import AdsManagerScreen from "../screens/dashboard/AdsManagerScreen";
import StudioAdsScreen from "../screens/dashboard/StudioAdsScreen";

import HeaderContainer from "../components/HeaderContainer";
import DashboardSideBar from "../components/DashboardSideBar";

const screenPathMap = {
	DashboardStack: "/dashboard",
	SigninScreen: "/signin",
	ForgotPasswordScreen: "/forgot-password",
	ResetPasswordScreen: "/reset-password",
	SetNewPasswordScreen: "/set-new-password",
	DashboardScreen: "/dashboard",
	BookingsScreen: "/bookings",
	EarningsScreen: "/earnings",
	MessagesScreen: "/messages",
	RoomsScreen: "/rooms",
	SettingsScreen: "/settings",
	HelpScreen: "/help",
	NewStudioScreen: "/studios/new",
	NewRoomScreen: "/rooms/new",
	StudiosScreen: "/studios",
	NotificationsScreen: "/notifications",
	ClientsScreen: "/clients",
	DataScreen: "/data",
	RevenueScreen: "/revenue",
	ReportsScreen: "/reports",
	PayoutsScreen: "/payouts",
	AdsManagerScreen: "/ads-manager",
	StudioAdsScreen: "/studio-ads",
};

const createNavigationAdapter = (navigate) => ({
	navigate: (target, params) => {
		const path = screenPathMap[target] || target;
		navigate(path, {state: params ? {params} : undefined});
	},
});

const RouteScreen = ({Component}) => {
	const navigate = useNavigate();
	const location = useLocation();
	return (
		<Component
			navigation={createNavigationAdapter(navigate)}
			route={{name: location.pathname, params: location.state?.params}}
		/>
	);
};

const DashboardLayout = ({children}) => {
	return (
		<div className="min-h-screen bg-[#f9f9f9] pt-20">
			<HeaderContainer />
			<div className="fixed bottom-0 left-0 top-20 z-10 hidden w-[280px] overflow-hidden lg:block">
				<DashboardSideBar />
			</div>
			<div className="min-h-[calc(100vh-5rem)] lg:ml-[280px]">{children}</div>
		</div>
	);
};

const AuthRoutes = () => (
	<Routes>
		<Route path="/signin" element={<RouteScreen Component={SigninScreen} />} />
		<Route path="/forgot-password" element={<RouteScreen Component={ForgotPasswordScreen} />} />
		<Route path="/reset-password" element={<RouteScreen Component={ResetPasswordScreen} />} />
		<Route path="/set-new-password" element={<RouteScreen Component={SetNewPasswordScreen} />} />
		<Route path="*" element={<Navigate to="/signin" replace />} />
	</Routes>
);

const DashboardRoutes = () => (
	<DashboardLayout>
		<Routes>
			<Route path="/dashboard" element={<RouteScreen Component={DashboardScreen} />} />
			<Route path="/bookings" element={<RouteScreen Component={BookingsScreen} />} />
			<Route path="/earnings" element={<RouteScreen Component={EarningsScreen} />} />
			<Route path="/messages" element={<RouteScreen Component={MessagesScreen} />} />
			<Route path="/rooms" element={<RouteScreen Component={RoomsScreen} />} />
			<Route path="/settings" element={<RouteScreen Component={SettingsScreen} />} />
			<Route path="/help" element={<RouteScreen Component={HelpScreen} />} />
			<Route path="/studios/new" element={<RouteScreen Component={NewStudioScreen} />} />
			<Route path="/rooms/new" element={<RouteScreen Component={NewRoomScreen} />} />
			<Route path="/studios" element={<RouteScreen Component={StudiosScreen} />} />
			<Route path="/notifications" element={<RouteScreen Component={NotificationsScreen} />} />
			<Route path="/clients" element={<RouteScreen Component={ClientsScreen} />} />
			<Route path="/data" element={<RouteScreen Component={DataScreen} />} />
			<Route path="/revenue" element={<RouteScreen Component={RevenueScreen} />} />
			<Route path="/reports" element={<RouteScreen Component={ReportsScreen} />} />
			<Route path="/payouts" element={<RouteScreen Component={PayoutsScreen} />} />
			<Route path="/ads-manager" element={<RouteScreen Component={AdsManagerScreen} />} />
			<Route path="/studio-ads" element={<RouteScreen Component={StudioAdsScreen} />} />
			<Route path="*" element={<Navigate to="/dashboard" replace />} />
		</Routes>
	</DashboardLayout>
);

const RootRouter = ({auth, dispatch}) => {
	const navigate = useNavigate();

	useEffect(() => {
		OneSignal.init({appId: "e3076dc2-3fb9-4b84-9bdf-737566e34e9b"});
		OneSignal.isPushNotificationsEnabled((isEnabled) => {
			if (isEnabled) console.log("Push notifications are enabled!");
			else console.log("Push notifications are not enabled yet.");
		});
		OneSignal.showSlidedownPrompt();

		if (auth.isLogged) {
			OneSignal.setExternalUserId(auth.user.id);
			OneSignal.on("notificationDisplay", (event) => {
				console.warn("OneSignal notification displayed:", event);
			});
			OneSignal.on("permissionPromptDisplay", () => {
				console.log("The prompt displayed");
			});
			OneSignal.on("notificationDismiss", (event) => {
				if (event?.data?.type === "ChatMessageWasReceived") {
					navigate(screenPathMap.MessagesScreen);
				}
			});
			OneSignal.on("subscriptionChange", (isSubscribed) => {
				console.log("The user's subscription state is now:", isSubscribed);
			});

			const echoServer = new Echo({
				broadcaster: "socket.io",
				host: "https://admin.stugo.com",
				transports: ["websocket", "polling", "flashsocket"],
				client: Socketio,
				auth: {
					headers: {
						Accept: "application/json",
						Authorization: `Bearer ${auth.accessToken}`,
					},
				},
			});

			const channel = echoServer.private(`App.Models.User.${auth.user.id}`);
			channel.listen(".App\\Events\\ChatMessageWasReceived", (callback) => {
				dispatch({type: "NEW_MESSAGE_WAS_RECEIVED", content: callback.message});
			});
		}
	}, [auth.isLogged, auth.user?.id, auth.accessToken, dispatch, navigate]);

	return (
		<>
			{auth.isLogged ? <DashboardRoutes /> : <AuthRoutes />}
			<ToastContainer hideProgressBar />
		</>
	);
};

const AppNavigator = ({auth, dispatch}) => (
	<BrowserRouter>
		<RootRouter auth={auth} dispatch={dispatch} />
	</BrowserRouter>
);

export default connect(({auth}) => ({auth}))(AppNavigator);
